import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import VideoSearch from './VideoSearch';

interface VideoSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVideoSelect: (videoId: string, title: string) => void;
}

const VideoSearchModal = ({ isOpen, onClose, onVideoSelect }: VideoSearchModalProps) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      searchInputRef.current?.focus();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus trap
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal || !isOpen) return;

    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0] as HTMLElement;
    const lastFocusable = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    modal.addEventListener('keydown', handleTabKey);
    return () => modal.removeEventListener('keydown', handleTabKey);
  }, [isOpen]);

  const handleAnimationEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const handleVideoSelect = (videoId: string, title: string) => {
    onVideoSelect(videoId, title);
    onClose();
  };

  return (
    <div 
      className={`fixed inset-0 z-50 overflow-y-auto ${isOpen ? 'animate-fadeIn' : 'animate-fadeOut'}`}
      onAnimationEnd={handleAnimationEnd}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-300 ${
          isOpen ? 'bg-opacity-60' : 'bg-opacity-0'
        }`}
      />

      {/* Modal Container - Add onClick handler here */}
      <div 
        className="relative min-h-screen flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div 
          ref={modalRef}
          className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl transform transition-all duration-300 ${
            isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
          }`}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 
              id="modal-title"
              className="text-xl font-semibold text-gray-900 dark:text-gray-100"
            >
              Search Videos
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <VideoSearch onVideoSelect={handleVideoSelect} ref={searchInputRef} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoSearchModal; 